import {Component, Inject, OnInit, PLATFORM_ID} from '@angular/core';
import Chart from 'chart.js/auto';
import {Market} from 'src/app/core/models/market.model';
import {AppService} from 'src/app/core/services/app.service';
import {HighlightedMarket} from 'src/app/core/models/highlighted-market.model';
import {Router} from '@angular/router';
import {DOCUMENT, isPlatformBrowser} from "@angular/common";


@Component({
  selector: 'bit-market-list',
  templateUrl: './market-list.component.html',
  styleUrls: ['./market-list.component.scss']
})
export class MarketListComponent implements OnInit {
  markets?: Market[];
  quoteCurrency: 'IRR' | 'USDT' = 'IRR';
  test = new Array(10).fill(0);
  skeletonElements = new Array(15).fill(0)
  url = this.router.url;

  constructor(
    private appService: AppService,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string
  ) {
  }

  generateMarketsChart(): void {
    // Render Currency changes charts after ngFor is done rendering
    for (const market of this.markets ?? []) {
      if (market.chart) {
        market.chart.destroy();
      }

      const leadingZeros = new Array(169 - market.data.length).fill(0).map((val, i) => ({
        x: i,
        y: null
      }));
      const chartData = market.data.map((data: any, index: any) => ({
        x: index + leadingZeros.length,
        y: +data.price,
      }));
      const data = {
        datasets: [
          {
            // @ts-ignore
            data: leadingZeros.concat(chartData),
            fill: true,
            cubicInterpolationMode: 'monotone',
            tension: 0.3,
            borderWidth: 2,
            borderColor:
              market.weeklyPercent >= 0
                ? 'rgba(0, 214, 143, 1)'
                : 'rgba(255,61,113,1)',
            pointRadius: 0,
            backgroundColor: (context: any) => {
              const chart = context.chart;
              const {ctx, chartArea} = chart;

              if (!chartArea) {
                // This case happens on initial chart load
                return null;
              }
              const gradient = ctx.createLinearGradient(
                0,
                chartArea.top,
                0,
                chartArea.bottom
              );
              gradient.addColorStop(
                0,
                market.weeklyPercent >= 0
                  ? 'rgba(0, 214, 143, 0.9)'
                  : 'rgba(255,61,113,0.9)'
              );
              gradient.addColorStop(
                1,
                market.weeklyPercent >= 0
                  ? 'rgba(0, 214, 143, 0)'
                  : 'rgba(255,61,113,0)'
              );
              return gradient;
            },
          },
        ],
      };
      const config = {
        type: 'line',
        data,
        options: {
          spanGaps: true,
          animation: false,
          parsing: false,
          datasets: {
            line: {
              pointRadius: 0, // disable for all `'line'` datasets
            },
          },
          hover: {mode: null},
          elements: {
            point: {
              radius: 0, // default to disabled in all datasets
            },
          },
          scales: {
            x: {
              display: false,
              type: 'linear',
              min: 0,
              max: 168,
            },
            y: {
              display: false,
              type: 'linear',
              min: Math.max(...market.data.map((data) => +data.price)),
              max: Math.min(...market.data.map((data) => +data.price)),
            },
          },

          responsive: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
            },
          },
        },
      };
      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          const element = this.document.getElementById('chart' + market.id);
          if (element) {
            market.chart = new Chart(element as any, config as any);
          }

        });
      }

    }
  }

  getMarketList(quoteCurrency: 'IRR' | 'USDT') {
    this.quoteCurrency = quoteCurrency;
    this.markets = undefined;
    this.appService.getMarketList(quoteCurrency).subscribe((markets) => {
      this.markets = markets;
      if (this.router.url === '/') {
        this.markets = markets.filter((v, i) => i < 10);
      }
      const highlightedMarket = {
        quoteCurrency: this.quoteCurrency,
        maximumVolume24h: {
          volume24: 0
        } as Market,
        maximumPriceRise24h: {
          percent: -1
        } as Market,
        newestMarket: {
          createdAt: '2000-01-01 00:00:00'
        } as Market
      } as HighlightedMarket;


      for (const market of markets) {
        if (quoteCurrency === 'IRR') {
          market.lastPrice = +market.lastPrice / 10
          market.volume24 = Math.floor(+market.volume24 / 10)
        }
        if (highlightedMarket.maximumVolume24h.volume24 < market.volume24) {
          highlightedMarket.maximumVolume24h = market;
        }
        if (highlightedMarket.maximumPriceRise24h.percent < market.percent) {
          highlightedMarket.maximumPriceRise24h = market;
        }
        if (new Date(Date.parse(highlightedMarket.newestMarket.createdAt.replace(/-/g, '/'))) <
          new Date(Date.parse(market.createdAt.replace(/-/g, '/')))) {
          highlightedMarket.newestMarket = market;
        }
      }
      this.appService.highlightedMarkets.next(highlightedMarket)
      if (isPlatformBrowser(this.platformId)) {
        this.generateMarketsChart();
      }
    }, () => {
    })
  }

  ngOnInit(): void {

    this.getMarketList('IRR');
  }

}
